import * as React from 'react'

import { FetchCycleDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const RootPage = React.lazy(() => import('modules/compensations/pages/settings/RootPage'))

const SettingsPage = React.lazy(() => import('modules/compensations/pages/settings/SettingsPage'))

const PayrollConcepts = React.lazy(
  () => import('modules/compensations/pages/settings/PayrollConcepts')
)
const EmployeeUpdatesPage = React.lazy(
  () => import('modules/compensations/pages/settings/EmployeeUpdates')
)

const A3nomIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/A3nomIntegration')
)

const SilaeIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/SilaeIntegration')
)
const DatevIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/DatevIntegration')
)
const DatevApiIntegration = React.lazy(
  () => import('modules/payroll_integrations_datev_api/pages/Settings')
)
const PaierhIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/PaierhIntegration')
)
const YeapPaierhIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/YeapPaierhIntegration')
)
const ZucchettiIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/ZucchettiIntegration')
)
const JobSistemiIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/JobSistemiIntegration')
)
const Sage100Integration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/Sage100Integration')
)
const GispagheIntegration = React.lazy(
  () => import('modules/payroll_bundle/pages/settings/GispagheIntegration')
)
const TimeReimbursementPage = React.lazy(
  () => import('modules/compensations/pages/settings/TimeReimbursementsPage')
)
const ExpensesReimbursementPage = React.lazy(
  () => import('modules/compensations/pages/settings/ExpensesReimbursementsPage')
)

const BenefitsPage = React.lazy(() => import('modules/compensations/pages/settings/BenefitsPage'))

const RedirectToTab = React.lazy(() => import('modules/compensations/pages/settings/RedirectToTab'))

const CyclePage = React.lazy(() => import('modules/compensations/pages/settings/CyclePage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.compensations.path,
      element: <RootPage />,
      handle: {
        meta: () => getSettingPageMeta(navigation, 'compensations'),
      },
      children: [
        {
          index: true,
          element: <SettingsPage />,
        },
        {
          path: navigation.settings.compensations.payrollConcepts.path,
          element: <PayrollConcepts />,
          handle: {
            meta: () => ({
              illustration: 'settings',
              title: i18n.t('compensations.settings.concepts.section.title'),
              description: i18n.t('compensations.settings.concepts.section.description'),
              link: {
                href: '#',
                label: i18n.t('compensations.settings.help'),
              },
            }),
          },
        },
        {
          path: navigation.settings.compensations.employeeUpdates.path,
          element: <EmployeeUpdatesPage />,
          handle: {
            meta: () => ({
              title: i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.settings'),
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationA3nom.path,
          element: <A3nomIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3nom.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_a3nom.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationDatev.path,
          element: <DatevIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_datev.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_datev.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationDatevApi.path,
          element: <DatevApiIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_datev_api.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_datev_api.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationZucchetti.path,
          element: <ZucchettiIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_zucchetti.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_zucchetti.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationJobSistemi.path,
          element: <JobSistemiIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_job_sistemi.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_job_sistemi.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationSage100.path,
          element: <Sage100Integration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_sage100.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_sage100.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationPaierh.path,
          element: <PaierhIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_paierh.general.title'
              ),
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationYeapPaierh.path,
          element: <YeapPaierhIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_yeap_paierh.general.title'
              ),
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationYeapPaierh.path,
          element: <YeapPaierhIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_yeap_paierh.general.title'
              ),
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationSilae.path,
          element: <SilaeIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_silae.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_silae.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.integrationGispaghe.path,
          element: <GispagheIntegration />,
          handle: {
            meta: () => ({
              title: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_gispaghe.general.title'
              ),
              description: i18n.t(
                'app_store_v2.app_integrations.payroll_integrations_gispaghe.general.description'
              ),
              illustration: 'smart-document',
            }),
          },
        },
        {
          path: navigation.settings.compensations.timeReimbursement.path,
          element: <TimeReimbursementPage />,
          handle: {
            meta: () => ({
              title: i18n.t('compensations.settings.time_reimbursements.breadcrumb'),
            }),
          },
        },
        {
          path: navigation.settings.compensations.expensesReimbursement.path,
          element: <ExpensesReimbursementPage />,
          handle: {
            meta: () => ({
              title: i18n.t('compensations.settings.expenses_reimbursements.breadcrumb'),
            }),
          },
        },
        {
          path: navigation.settings.compensations.benefits.path,
          element: <BenefitsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('compensations.settings.benefits.breadcrumb'),
            }),
          },
        },
        {
          path: navigation.settings.compensations.cycles.path,
          element: <RootPage />,
          handle: {
            meta: () => ({
              title: i18n.t('compensations.settings.cycles.breadcrumb'),
            }),
          },
          children: [
            {
              index: true,
              element: <RedirectToTab />,
            },
            {
              path: navigation.settings.compensations.cycles.id.path,
              element: <CyclePage />,
              handle: {
                meta: ({ params, watchQuery }) => {
                  return watchQuery({
                    query: FetchCycleDocument,
                    variables: {
                      cycleId: Number(params.id),
                    },
                  }).map(({ data }) => {
                    const cycle = data?.compensations?.cyclesConnection.nodes.find(
                      (it) => it.id === Number(params.id)
                    )
                    return {
                      title: cycle?.countryLabel,
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
